import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Star, ExternalLink, ArrowLeft, Check, Loader,
  Footprints, Shirt, Backpack, Smartphone, Droplet, CloudRain,
  Heart, Sparkles, Package,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEOHead from '../components/SEOHead';
import walkUrl from '../utils/walkUrl';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const CATEGORY_ICON = {
  Footwear: Footprints,
  Apparel: Shirt,
  Backpacks: Backpack,
  Bags: Package,
  Tech: Smartphone,
  Hydration: Droplet,
  Weather: CloudRain,
  Recovery: Heart,
  Extras: Sparkles,
};

const GearDetailPage2 = () => {
  const { slug } = useParams();
  const { isAuthenticated } = useAuth();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND}/api/gear/${slug}`);
        setItem(res.data);
      } catch {
        setError('Gear item not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="flex items-center justify-center py-24"><Loader className="w-8 h-8 animate-spin text-gray-400" /></div>
      <Footer />
    </div>
  );

  if (error || !item) return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <p className="text-gray-600 mb-4">{error || 'Item not found'}</p>
        <Link to="/gear"><Button>Back to Gear</Button></Link>
      </div>
      <Footer />
    </div>
  );

  const priceTierColor =
    item.price_tier === 'Budget' ? 'bg-green-100 text-green-800 hover:bg-green-100' :
    item.price_tier === 'Premium' ? 'bg-amber-100 text-amber-800 hover:bg-amber-100' :
    item.price_tier === 'Mid' ? 'bg-blue-100 text-blue-800 hover:bg-blue-100' :
    'bg-gray-100 text-gray-700';

  const relatedWalks = item.relatedWalks || [];
  const CategoryIcon = CATEGORY_ICON[item.category] || Package;
  const walkTypeTags = item.walk_type_tags || [];

  // Shop button setup. Prefer affiliate_links[0].retailer (works for legacy items that
  // have an Amazon shortlink but no amazon_asin). Fall back to amazon_asin presence,
  // then brand as last resort. Hide entirely if no affiliate_url at all.
  const retailer =
    item.affiliate_links?.[0]?.retailer
    || (item.amazon_asin ? 'Amazon' : item.brand);
  const isAmazon = retailer === 'Amazon';
  const hasShopLink = Boolean(item.affiliate_url);
  const shopLabel = `Shop at ${retailer}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead title={`${item.name} by ${item.brand} — Gear Review`} description={item.bens_take} />
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <ol className="flex items-center gap-2 text-sm text-gray-500">
            <li><Link to="/" className="hover:text-gray-700">Home</Link></li>
            <li>/</li>
            <li><Link to="/gear" className="hover:text-gray-700">Gear</Link></li>
            <li>/</li>
            <li className="text-gray-900 font-medium">{item.name}</li>
          </ol>
        </nav>

        {/* Hero Card */}
        <div className="bg-white rounded-xl border mb-6 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="bg-white flex items-center justify-center min-h-[380px] p-6">
              {item.images?.[0] ? (
                <img src={item.images[0]} alt={item.name} className="w-full h-full max-h-[450px] object-contain" />
              ) : (
                <div className="w-full h-full bg-gray-100 rounded flex flex-col items-center justify-center min-h-[280px] text-gray-400">
                  <CategoryIcon className="w-20 h-20 mb-2" strokeWidth={1.5} />
                  <p className="text-sm uppercase tracking-wide">{item.category}</p>
                </div>
              )}
            </div>
            <div className="p-6 md:p-8 flex flex-col justify-center">
              <p className="text-sm text-gray-500 font-medium">{item.brand}</p>
              <h1 className="text-3xl font-bold text-gray-900 mb-3">{item.name}</h1>
              <div className="flex items-center gap-4 mb-4">
                {item.rating != null && (
                  <div className="flex items-center gap-1">
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <span className="font-semibold text-lg">{item.rating}</span>
                    <span className="text-gray-400">/ 5.0</span>
                  </div>
                )}
                <span className="text-2xl font-bold text-gray-900">${item.price}</span>
                {item.price_tier && <Badge className={priceTierColor}>{item.price_tier}</Badge>}
              </div>
              <div className="flex flex-wrap gap-2">
                {(item.tags || []).map(tag => (
                  <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Ben's Take — matches Walk Personality gradient card style */}
        {item.bens_take && (
          <Card className="mb-6 bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 border-none shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xl">💬</span>
                </div>
                <div>
                  <h2 className="font-bold text-gray-900 mb-2">Ben's Take</h2>
                  <p className="text-gray-700 italic leading-relaxed">"{item.bens_take}"</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Where to Buy — brand-aware. Hidden when no affiliate_url. */}
        {hasShopLink && (
          <Card className="mb-6">
            <CardHeader><CardTitle>Where to Buy</CardTitle></CardHeader>
            <CardContent>
              <a href={item.affiliate_url} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black rounded-lg font-bold transition-all mb-3">
                {shopLabel}
                <ExternalLink className="w-4 h-4" />
              </a>
              {isAmazon && (
                <p className="text-xs text-gray-400 italic">
                  As an Amazon Associate, Wanderloop earns from qualifying purchases.
                </p>
              )}
              {isAuthenticated && item.affiliate_note && (
                <p className="text-xs text-gray-400 italic mt-1">
                  {item.affiliate_note}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Full Review */}
        {item.full_review && (
          <Card className="mb-6">
            <CardHeader><CardTitle>Full Review</CardTitle></CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed">{item.full_review}</p>
            </CardContent>
          </Card>
        )}

        {/* Key Features — colored stat cards like Walk Highlights summary */}
        {item.key_features?.length > 0 && (
          <Card className="mb-6">
            <CardHeader><CardTitle>Key Features</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {item.key_features.map((feat, i) => {
                  const colors = ['bg-blue-50 text-blue-700', 'bg-green-50 text-green-700', 'bg-purple-50 text-purple-700', 'bg-orange-50 text-orange-700'];
                  return (
                    <div key={i} className={`${colors[i % colors.length]} rounded-lg p-3 text-center`}>
                      <Check className="w-5 h-5 mx-auto mb-1 opacity-60" />
                      <p className="text-sm font-medium">{feat}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Best for: walk_type_tags chips */}
        {walkTypeTags.length > 0 && (
          <Card className="mb-6">
            <CardHeader><CardTitle>Best for</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {walkTypeTags.map(tag => (
                  <span key={tag} className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    {tag.charAt(0).toUpperCase() + tag.slice(1)}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Compare to Alternatives — styled like Walk Highlights POI cards */}
        {item.alternatives?.length > 0 && (
          <Card className="mb-6">
            <CardHeader><CardTitle>Compare to Alternatives</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {item.alternatives.map((alt, i) => (
                  <div key={i} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all hover:bg-blue-50/30">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-bold">vs</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{alt.name}</p>
                        <p className="text-sm text-gray-500 mt-0.5">{alt.note}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Seen in These Walks */}
        {relatedWalks.length > 0 && (
          <Card className="mb-6">
            <CardHeader><CardTitle>Seen in These Walks</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {relatedWalks.map((walk, i) => (
                  <Link key={i} to={walkUrl(walk)}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-blue-50 transition-colors">
                    <span className="font-medium text-blue-600">{walk.title}</span>
                    <span className="text-xs text-gray-500">
                      {walk.difficulty}
                      {walk.distance_meters ? ` · ${(walk.distance_meters / 1609.34).toFixed(1)} mi` : ''}
                    </span>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Specs — colored stat cards like Walk Highlights summary */}
        <Card className="mb-6">
          <CardHeader><CardTitle>Quick Specs</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="font-semibold text-lg text-blue-900">{item.category}</div>
                <div className="text-blue-700 text-sm">Category</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <div className="font-semibold text-lg text-green-900">{item.price_tier}</div>
                <div className="text-green-700 text-sm">Price Tier</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 text-center">
                <div className="font-semibold text-lg text-purple-900">{item.rating != null ? `${item.rating}/5.0` : '—'}</div>
                <div className="text-purple-700 text-sm">Rating</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center py-4">
          <Link to="/gear">
            <Button variant="outline"><ArrowLeft className="w-4 h-4 mr-2" /> Back to All Gear</Button>
          </Link>
        </div>
      </div>

      <FloatingActions />
      <Footer />
    </div>
  );
};

export default GearDetailPage2;
